/**
 * V2Root — the Portal V2 root. Everything V2 renders sits under one of these.
 *
 * It does three things, all ported from the design package's index.html
 * (refactor/design-sync/portal-free-app-v2/…/portal-free-app-v2/index.html):
 *   1. wraps the tree in `HubThemeScope`, so any MUI canonical inside (and its
 *      portals — MUI theme context crosses them) takes the hub grammar;
 *   2. renders ONE style block holding every V2 keyframe and helper class, each
 *      renamed with a `v2-` prefix (the app already owns `slbPop`, `dnTick`,
 *      `qWipe`, `qPulse` and `dnDraw` with different values) and scoped under
 *      `.v2-root`, plus the design's reduced-motion rule;
 *   3. in `mode="night"` applies V2's night map (`V2_NIGHT_TOKENS`) as inline
 *      custom properties on its own element, leaving the staff app's dark map
 *      alone.
 *
 * Nested roots (a portaled overlay that re-wraps itself to pick up the class and
 * the night tokens) skip both the style block and the theme scope: the block is
 * document-global once rendered, and the theme already flows through the portal.
 */
import React, { createContext, useContext } from 'react';
import { HubThemeScope } from '../clients/hub/designSystem';
import { V2_NIGHT_TOKENS, V2_TOKENS } from '../../theme/cssVariables';

export type V2Mode = 'day' | 'night';

/* index.html:9-98, verbatim values; names prefixed `v2-`, selectors scoped to `.v2-root`. */
export const V2_STYLES = `
.v2-root{color:var(--ink);font-family:var(--font-body);-webkit-font-smoothing:antialiased}
.v2-root,.v2-root *{box-sizing:border-box}
.v2-root input,.v2-root button,.v2-root select,.v2-root textarea{font-family:var(--font-body)}
.v2-root input::placeholder,.v2-root textarea::placeholder{color:var(--ink-ghost)}
.v2-root a{color:var(--indigo-ink);text-decoration-color:color-mix(in srgb,var(--indigo-ink) 35%,transparent)}
.v2-root a:hover{color:var(--indigo)}
.v2-root .v2-scroll::-webkit-scrollbar{width:9px;height:9px}
.v2-root .v2-scroll::-webkit-scrollbar-thumb{background:var(--rule-strong);border-radius:6px;border:2px solid transparent;background-clip:padding-box}
.v2-root .v2-scroll::-webkit-scrollbar-track{background:transparent}
.v2-root .v2-nobar::-webkit-scrollbar{height:0;width:0}
.v2-root button{transition:transform 120ms var(--e-out),background 120ms ease,border-color 120ms ease,color 120ms ease,box-shadow 120ms ease,opacity 120ms ease}
.v2-root button:active:not(:disabled){transform:scale(.975)}
.v2-root .v2-hv:hover{background:var(--paper-2)!important}
.v2-root .v2-row{transition:background 120ms ease,box-shadow 120ms ease}
.v2-root .v2-row:hover{background:var(--hover)!important}
.v2-root .v2-row:hover .v2-kebab{opacity:1!important}
.v2-root .v2-lift{transition:transform 180ms var(--e-out),box-shadow 180ms var(--e-out),border-color 120ms ease}
.v2-root .v2-lift:hover{transform:translateY(-1px);box-shadow:0 8px 22px -12px rgba(4,67,239,.4)}
.v2-root .v2-field:focus-within{border-color:var(--indigo)!important;box-shadow:0 0 0 3px var(--indigo-soft)}
.v2-root .v2-field input,.v2-root .v2-field textarea{outline:none}
.v2-root .v2-item:hover{background:var(--indigo-soft)!important}
.v2-root .v2-item:not([data-danger="true"]):hover{color:var(--indigo-ink)!important}
.v2-root .v2-tab:hover{color:var(--ink)!important}
.v2-root .v2-link:hover{text-decoration:underline}
@keyframes v2-fadeUp{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:none}}
.v2-root .v2-fadeUp{animation:v2-fadeUp 450ms var(--e-out) both}
@keyframes v2-fadeK{from{opacity:0}to{opacity:1}}
.v2-root .v2-fadeIn{animation:v2-fadeK 300ms ease-out both}
@keyframes v2-hubDialogRise{from{opacity:0;transform:translateY(10px) scale(.985)}to{opacity:1;transform:none}}
.v2-root .v2-hubDialogRise{animation:v2-hubDialogRise 200ms var(--e-out) both}
@keyframes v2-dnDim{from{opacity:0}to{opacity:1}}
.v2-root .v2-dnDim{animation:v2-dnDim 180ms ease-out both}
@keyframes v2-dnSlide{from{opacity:0;transform:translateX(24px)}to{opacity:1;transform:none}}
.v2-root .v2-dnSlide{animation:v2-dnSlide 180ms var(--e-out) both}
@keyframes v2-dnSlideUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
.v2-root .v2-dnSlideUp{animation:v2-dnSlideUp 180ms var(--e-out) both}
@keyframes v2-dnSpring{from{opacity:0;transform:translateY(-5px) scale(.97)}to{opacity:1;transform:none}}
.v2-root .v2-dnSpring{animation:v2-dnSpring 140ms var(--e-out) both}
@keyframes v2-slbPop{from{opacity:0;transform:translateY(6px) scale(.99)}to{opacity:1;transform:none}}
.v2-root .v2-slbPop{animation:v2-slbPop 190ms var(--e-out) both}
@keyframes v2-wallIn{from{opacity:0;transform:translateY(9px)}to{opacity:1;transform:none}}
.v2-root .v2-wallIn{animation:v2-wallIn 380ms var(--e-out) both}
@keyframes v2-dnTickK{from{stroke-dashoffset:26}to{stroke-dashoffset:0}}
.v2-root .v2-dnTick{stroke-dasharray:26;animation:v2-dnTickK 280ms var(--e-out) both}
@keyframes v2-dnBadgepop{0%{transform:scale(.6);opacity:0}60%{transform:scale(1.12)}100%{transform:scale(1);opacity:1}}
.v2-root .v2-dnBadgepop{animation:v2-dnBadgepop 300ms var(--e-over) both}
@keyframes v2-qWipe{from{opacity:0;clip-path:inset(0 100% 0 0)}to{opacity:1;clip-path:inset(0 0 0 0)}}
/* no fill mode: a retained clip-path would clip (and trap) the drawers and dialogs inside it */
.v2-root .v2-qWipe{animation:v2-qWipe 360ms var(--e-out)}
@keyframes v2-qPulse{0%,100%{opacity:1}50%{opacity:.4}}
.v2-root .v2-qPulse{animation:v2-qPulse 1600ms ease-in-out infinite}
@keyframes v2-dnDrawK{from{stroke-dashoffset:var(--len,300)}to{stroke-dashoffset:0}}
.v2-root .v2-dnDraw{animation:v2-dnDrawK 900ms var(--e-out) both}
@keyframes v2-skelSweep{from{background-position:-160% 0}to{background-position:260% 0}}
.v2-root .v2-skel{background:var(--v2-skel-sweep);background-size:220% 100%;animation:v2-skelSweep 1400ms linear infinite}
@keyframes v2-rmIn{from{opacity:0;transform:scale(.9) translateY(5px)}to{opacity:1;transform:none}}
.v2-root .v2-rmIn{animation:v2-rmIn 380ms var(--e-out) both}
@keyframes v2-rmDrawK{from{stroke-dashoffset:1}to{stroke-dashoffset:0}}
.v2-root .v2-rmDraw{stroke-dasharray:1;animation:v2-rmDrawK 700ms var(--e-out) both}
@keyframes v2-rmPulseK{0%,100%{opacity:1}50%{opacity:.45}}
.v2-root .v2-rmPulse{animation:v2-rmPulseK 2.2s ease-in-out infinite}
@keyframes v2-rmPopK{from{opacity:.9;transform:rotate(45deg) scale(.6)}to{opacity:0;transform:rotate(45deg) scale(3.2)}}
.v2-root .v2-rmPop{animation:v2-rmPopK 800ms var(--e-out) both}
@keyframes v2-rmPanelK{from{opacity:0;transform:scale(.82)}to{opacity:1;transform:scale(1)}}
.v2-root .v2-rmPanel{animation:v2-rmPanelK 300ms var(--e-out) both}
@keyframes v2-rmFadeK{from{opacity:0}to{opacity:1}}
.v2-root .v2-rmFadeIn{animation:v2-rmFadeK 400ms ease-out both}
@keyframes v2-rmSpinK{from{transform:rotate(0)}to{transform:rotate(360deg)}}
.v2-root .v2-rmSpin{animation:v2-rmSpinK 160s linear infinite}
.v2-root .v2-rmSpinR{animation:v2-rmSpinK 90s linear infinite reverse}
.v2-root .v2-rmSpinSlow{animation:v2-rmSpinK 40s linear infinite}
.v2-root .v2-rmSpider{animation:v2-rmIn 460ms var(--e-over) both}
.v2-root .v2-rmRise{animation:v2-fadeUp 140ms var(--e-out) both}
@media (prefers-reduced-motion:reduce){
.v2-root,.v2-root *,.v2-root *::before,.v2-root *::after{animation-duration:.001ms!important;animation-iteration-count:1!important;transition-duration:.001ms!important}
.v2-root .v2-dnTick,.v2-root .v2-dnDraw,.v2-root .v2-rmDraw{stroke-dashoffset:0!important}}
`;

const V2RootContext = createContext<{ mode: V2Mode } | null>(null);

/** The mode of the nearest `V2Root` (`'day'` outside one). Portaled V2 overlays read
 *  it to re-wrap their content in `<V2Root mode={useV2Mode()}>`. */
export function useV2Mode(): V2Mode {
  return useContext(V2RootContext)?.mode ?? 'day';
}

export interface V2RootProps {
  /** `'night'` applies V2's night token map on this element. Default `'day'` inherits the app's tokens. */
  mode?: V2Mode;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export function V2Root({ mode = 'day', className, style, children }: V2RootProps): React.ReactElement {
  const parent = useContext(V2RootContext);
  const vars = (mode === 'night' ? { ...V2_TOKENS, ...V2_NIGHT_TOKENS } : V2_TOKENS) as React.CSSProperties;
  const body = (
    <V2RootContext.Provider value={{ mode }}>
      <div className={className ? `v2-root ${className}` : 'v2-root'} data-mode={mode} style={{ ...vars, ...style }}>
        {!parent && <style data-v2-styles="">{V2_STYLES}</style>}
        {children}
      </div>
    </V2RootContext.Provider>
  );
  return parent ? body : <HubThemeScope>{body}</HubThemeScope>;
}

export default V2Root;
